import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../config/firebase';
import { getTosConfig, useFreshTosConfig } from '../utils/termsConfig';


const SHOP_API_BASE_URL = process.env.REACT_APP_SHOP_API_BASE_URL || 'https://shop.dev.carouselshopai.com';

/**
 * Interstitial page shown to authenticated users who have not yet accepted
 * the current required agreements (ToS + Beta Terms). Blocks access to the
 * app until the user accepts.
 */
const AcceptTermsPage: React.FC = () => {
  const navigate = useNavigate();
  // Fetched fresh on open: the link and the recorded version are always the CURRENT one (null while loading).
  const tosConfig = useFreshTosConfig();
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleAccept = async () => {
    setSubmitting(true);
    setError('');

    try {
      const user = auth.currentUser;
      if (!user) {
        setError('You are not signed in. Please log in and try again.');
        return;
      }

      const token = await user.getIdToken();

      // Record exactly the version shop-svc says is current (and its versioned page), never a
      // hard-coded constant. getTosConfig never rejects; it falls back to the built-in version.
      const tos = await getTosConfig();
      const response = await fetch(
        `${SHOP_API_BASE_URL}/api/v1/user-agreements`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            agreements: [
              {
                agreementType: 'TERMS_OF_SERVICE',
                agreementVersion: tos.currentVersion,
                documentUrl: tos.documentUrl,
              },
            ],
            acceptedVia: 'tos_update_prompt',
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to record agreements:', errorText);
        setError('Failed to record agreement acceptance. Please try again.');
        return;
      }

      // Agreements recorded — proceed to dashboard
      navigate('/');
    } catch (err) {
      console.error('Error accepting terms:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-form">
          <h2>Terms Update</h2>
          <p className="login-subtitle">
            Please review and accept our terms to continue using Carousel Shop AI.
          </p>

          {error && <div className="error-message">{error}</div>}

          <div style={{ marginTop: '16px', marginBottom: '24px' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', fontSize: '0.8125rem', color: 'var(--text-primary)', lineHeight: 1.5 }}>
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                style={{ marginTop: '3px', flexShrink: 0 }}
              />
              <span>
                I agree to the{' '}
                <a href={tosConfig ? tosConfig.documentUrl : '/terms'} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-light)' }}>
                  Terms of Service
                </a>
              </span>
            </label>
          </div>

          <button
            className="login-btn"
            onClick={handleAccept}
            disabled={!agreedToTerms || submitting || !tosConfig}
          >
            {submitting ? 'Submitting...' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AcceptTermsPage;
